import { TextField } from '@material-ui/core'
import { AutocompleteRenderInputParams } from '@material-ui/lab'
import { graphql } from 'babel-plugin-relay/macro'
import { Field, useField } from 'formik'
import { Autocomplete } from 'formik-material-ui-lab'
import React, { useEffect, useRef, useState } from 'react'
import { fetchQuery, useRelayEnvironment } from 'react-relay/hooks'
import { VesselFieldQuery } from '../../__generated__/VesselFieldQuery.graphql'
import ItemImage from '../ItemImage'
import { FieldT, FieldVariantEnum } from './types'

interface Props {
  field: FieldT
  variant?: FieldVariantEnum
}

const VesselField: React.FC<Props> = ({ field }) => {
  const [, meta, helpers] = useField(field.name)
  const [searchQuery, setSearchQuery] = useState('')
  const searchQueryRef = useRef('')
  const environment = useRelayEnvironment()
  const [options, setOptions] = useState<any[]>([])

  // Fetch vessels based on user's search input
  useEffect(() => {
    fetchQuery<VesselFieldQuery>(environment, query, { type: 'vessels', searchQuery: searchQuery })
      .toPromise()
      .then((data) => data?.itemList.items.edges?.map((edge) => edge?.node) || [])
      .then((opts) => setOptions(opts as any[]))
  }, [searchQuery, environment])

  const onQueryChange = (newQuery: string) => {
    searchQueryRef.current = newQuery
    setTimeout(() => {
      if (searchQueryRef.current === newQuery) {
        setSearchQuery(newQuery)
      }
    }, 300)
  }

  // Find the selected option from the options array based on meta.value (slug)
  const selectedOption = meta.value ? options.find((opt: any) => opt?.slug === meta.value) : null

  const handleChange = (_: any, value: any) => {
    if (value) {
      // Ensure the selected vessel is in the options array
      setOptions((prevOptions) => {
        if (!prevOptions.some((opt: any) => opt?.slug === value.slug)) {
          return [...prevOptions, value]
        }
        return prevOptions
      })
      helpers.setValue(value.slug)
    } else {
      helpers.setValue(null)
    }
  }

  return (
    <Field
      name={field.name}
      component={Autocomplete}
      options={options}
      getOptionLabel={(option: any) => option?.title || ''}
      getOptionSelected={(option: any, value: any) => option?.slug === value?.slug}
      value={selectedOption || null}
      onChange={handleChange}
      onInputChange={(_e: any, value: string) => onQueryChange(value)}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          error={meta.touched && !!meta.error}
          helperText={meta.touched ? meta.error : null}
          label={field.title}
          variant='outlined'
        />
      )}
      renderOption={(option: any) => (
        <>
          <ItemImage data={option.image} variant='small' />
          {option.title}
        </>
      )}
    />
  )
}

const query = graphql`
  query VesselFieldQuery($type: ListItemTypeEnum!, $searchQuery: String) {
    itemList(type: $type, searchQuery: $searchQuery) {
      items {
        edges {
          node {
            slug
            title
            image {
              ...ItemImage_data
            }
          }
        }
      }
    }
  }
`

export default VesselField
